import { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { QuotationFormContext } from '../Context'
import { useProductLineStore } from 'Store/ProductLine'

import Swal from 'sweetalert2'
import { shallow } from 'zustand/shallow'
import { generateImageUrl } from 'Utils/generateImageUrl'
import { ProductionUnit, ThProductionUnit } from 'Configuration/ProductionUnit'
import { ProductionMeasurement, ThProductionMeasurement } from 'Configuration/ProductionMeasurement'
import * as Yup from 'yup'
import { toast } from 'react-toastify'

/**
 * Step 1 : Product Line
 * Step 2 : Properties
 * Step 3 : Dimension
 */

export interface Props {
	canSelectProductLine: boolean
	isSelectingProduct: boolean
	totalItems: number
	formType: 'create' | 'edit'
	selectedProduct: {
		product: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
			code: string
			quantity: number
			shortDescription: string

			productLine?: {
				id: number
				nameEn: string
				nameTh: string
				featurePicturePath: string | null
				parent?: {
					id: number
					nameEn: string
					nameTh: string
					featurePicturePath: string | null
				}
			}
		}

		dimension?: {
			width: number
			length: number
			height: number
			weightGram: number
			verticalStack: number
			unit: ProductionUnit | ThProductionUnit
			measurementApproach: ProductionMeasurement | ThProductionMeasurement
		}
		options?: {
			taxonomyProductLineOption: {
				id: number
				nameTh: string
				nameEn: string
				featurePicturePath: string
			}
		}[]
		accessories?: {
			taxonomyProductLineAccessory: {
				id: number
				nameTh: string
				nameEn: string
				featurePicturePath: string
			}
		}[]
		thickness?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		outerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		innerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		printingOption?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		forming?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	} | null
	setSelectingProduct: (selectingProduct: boolean) => void
	onConfirmAddProduct: (input: {
		product: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
			code: string
			quantity: number
			shortDescription: string

			productLine?: {
				id: number
				nameEn: string
				nameTh: string
				featurePicturePath: string | null
				parent?: {
					id: number
					nameEn: string
					nameTh: string
					featurePicturePath: string | null
				}
			}
		}

		dimension?: {
			width: number
			length: number
			height: number
			weightGram: number
			verticalStack: number
			unit: ProductionUnit | ThProductionUnit
			measurementApproach: ProductionMeasurement | ThProductionMeasurement
		}
		options?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}[]
		accessories?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}[]
		thickness?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		outerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		innerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		printingOption?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		forming?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}) => void
	onBack: () => void
}

const INITIAL_STATE_PROPERTY = {
	product: 0,
	options: [] as number[],
	accessories: [] as number[],
	thickness: 0,
	outerMaterial: 0,
	innerMaterial: 0,
	printingOption: 0,
	forming: 0,
	unit: 'mm' as ProductionUnit | ThProductionUnit,
	measurementApproach: 'product_size' as ProductionMeasurement | ThProductionMeasurement,
	width: null as number | null,
	length: null as number | null,
	height: null as number | null,
	weightGram: null as number | null,
	verticalStack: null as number | null,
}

const ViewModel = ({
	canSelectProductLine,
	isSelectingProduct,
	totalItems,
	formType,
	selectedProduct,
	setSelectingProduct,
	onConfirmAddProduct,
	onBack,
}: Props) => {
	const propertyValidationStep2 = Yup.object().shape({
		product: Yup.number().not([0]).required('กรุณาเลือกสินค้า'),
		options: Yup.array().required('กรุณาเลือกอย่างน้อย 1 ตัวเลือก'),
		accessories: Yup.array().required('กรุณาเลือกอย่างน้อย 1 อุปกรณ์'),
		thickness: Yup.number().required('กรุณาเลือกความหนา'),
		outerMaterial: Yup.number().required('กรุณาเลือกวัสดุภายนอก'),
		innerMaterial: Yup.number().required('กรุณาเลือกวัสดุภายใน'),
		printingOption: Yup.number().required('กรุณาเลือกตัวเลือกการพิมพ์'),
		forming: Yup.number().required('กรุณาเลือกวิธีการจัดรูป'),
	})
	const propertyValidationStep3 = Yup.object().shape({
		unit: Yup.string().required('กรุณาเลือกหน่วยวัด'),
		measurementApproach: Yup.string().required('กรุณาเลือกวิธีการวัด'),
		width: Yup.number().required('กรุณากรอกความกว้าง'),
		length: Yup.number().required('กรุณากรอกความยาว'),
		height: Yup.number().required('กรุณากรอกความสูง'),
		weightGram: Yup.number().required('กรุณากรอกน้ำหนัก (กรัม)'),
		verticalStack: Yup.number().required('กรุณากรอกจำนวนชั้น'),
	})

	const location = useLocation()
	const [currentStep, setCurrentStep] = useState(0)
	const productCategoryRef = useRef<any>(null)

	const [isLoadingProductLine, setIsLoadingProductLine] = useState(false)
	const [isLoadingProductCategory, setIsLoadingProductCategory] = useState(false)
	const [isLoadingProperties, setIsLoadingProperties] = useState(false)
	const {
		selectedProductLine,
		selectedProductCategory,
		setSelectedProductLine,
		setSelectedProductCategory,
	} = useContext(QuotationFormContext)

	const {
		selectedWithChildren,
		setSelectedWithChildren,
		setSelectedWithProperties,
		getOneWithChildren,
		getOneWithProperties,
		getProducts,
	} = useProductLineStore(
		state => ({
			selectedWithChildren: state.selectedWithChildren,
			selectedWithProperties: state.selectedWithProperties,
			products: state.products,
			setSelectedWithChildren: state.setSelectedWithChildren,
			setSelectedWithProperties: state.setSelectedWithProperties,
			getOneWithProperties: state.getOneWithProperties,
			getOneWithChildren: state.getOneWithChildren,
			getProducts: state.getProducts,
		}),
		shallow
	)
	const { data, getAll, clearProductLineState } = useProductLineStore(
		state => ({
			data: state.data,
			getAll: state.getAll,
			clearProductLineState: state.clearState,
		}),
		shallow
	)
	const productLineData = useMemo(() => {
		return data
			.filter((d: any) => d.parentId === null)
			.map((d: any) => {
				return {
					...d,
					featurePicturePath: d.featurePicturePath
						? generateImageUrl(d.featurePicturePath)
						: undefined,
					disabled: false,
					clickable: true,
				}
			})
	}, [data])
	const productCategoryData: any[] = useMemo(() => {
		if (selectedWithChildren?.child) {
			return selectedWithChildren?.child.map((d: any) => {
				return {
					...d,
					featurePicturePath: d.featurePicturePath
						? generateImageUrl(d.featurePicturePath)
						: undefined,
					disabled: false,
					clickable: true,
				}
			})
		} else {
			return []
		}
	}, [selectedWithChildren])

	const currentActiveProductLine = useMemo(() => {
		return productLineData.findIndex(x => x.id === selectedProductLine) ?? 0
	}, [selectedProductLine, productLineData])
	const currentActiveProductCategory = useMemo(() => {
		return productCategoryData.findIndex((x: any) => x.id === selectedProductCategory) ?? 0
	}, [selectedProductCategory, productCategoryData])

	const showPrevButton = useMemo(() => {
		if (currentStep === 0) {
			if (!canSelectProductLine) {
				return false
			} else {
				return true
			}
		} else {
			return true
		}
	}, [currentStep, canSelectProductLine])

	const [mappedProducts, setMappedProducts] = useState<any[]>([])
	const [mappedOptions, setMappedOptions] = useState<any[]>([])
	const [mappedAccessories, setMappedAccessories] = useState<any[]>([])
	const [mappedThicknesses, setMappedThicknesses] = useState<any[]>([])
	const [mappedOuterMaterials, setMappedOuterMaterials] = useState<any[]>([])
	const [mappedInnerMaterials, setMappedInnerMaterials] = useState<any[]>([])
	const [mappedPrintingOptions, setMappedPrintingOptions] = useState<any[]>([])
	const [mappedFormings, setMappedFormings] = useState<any[]>([])
	const [propertyState, setPropertyState] = useState(INITIAL_STATE_PROPERTY)

	const [errors, setErrors] = useState<any>({})

	const isCreate = location.pathname.includes('create')

	const handleStartSelectingProduct = () => {
		if (isSelectingProduct === true) {
			if (canSelectProductLine) {
				setCurrentStep(0)
				document
					.getElementById('product-form-quotation-anchor')
					?.scrollIntoView({ behavior: 'smooth' })
			} else {
				if (formType === 'edit') {
					const hasParent = selectedProduct?.product.productLine?.parent !== null

					setSelectedProductLine(selectedProduct?.product.productLine?.id ?? 0)
					setSelectedProductCategory(
						(hasParent
							? selectedProduct?.product.productLine?.parent?.id
							: selectedProduct?.product?.productLine?.id) ?? 0
					)
					setPropertyState({
						product: selectedProduct?.product.id ?? 0,
						options: selectedProduct?.options?.map(x => x.taxonomyProductLineOption.id) ?? [],
						accessories:
							selectedProduct?.accessories?.map(x => x.taxonomyProductLineAccessory.id) ?? [],
						thickness: selectedProduct?.thickness?.id ?? 0,
						outerMaterial: selectedProduct?.outerMaterial?.id ?? 0,
						innerMaterial: selectedProduct?.innerMaterial?.id ?? 0,
						printingOption: selectedProduct?.printingOption?.id ?? 0,
						forming: selectedProduct?.forming?.id ?? 0,
						unit: selectedProduct?.dimension?.unit ?? ('mm' as ProductionUnit | ThProductionUnit),
						measurementApproach:
							selectedProduct?.dimension?.measurementApproach ??
							('product_size' as ProductionMeasurement | ThProductionMeasurement),
						width: selectedProduct?.dimension?.width ?? null,
						length: selectedProduct?.dimension?.length ?? null,
						height: selectedProduct?.dimension?.height ?? null,
						weightGram: selectedProduct?.dimension?.weightGram ?? null,
						verticalStack: selectedProduct?.dimension?.verticalStack ?? null,
					})
					document
						.getElementById('product-form-quotation-anchor')
						?.scrollIntoView({ behavior: 'smooth' })
				}

				setCurrentStep(1)
			}
		}
	}

	const fetchProductLines = async () => {
		if (currentStep === 0) {
			setIsLoadingProductLine(true)

			// Product Line Masterdata
			getAll().then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'ดึงข้อมูลไม่สำเร็จ',
						text: data,
						confirmButtonText: 'ปิด',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					}).then(() => {
						setSelectingProduct(false)
					})
				} else {
					if (data.length > 0) {
						setSelectedProductLine(data[0]?.id)
					}

					setIsLoadingProductLine(false)
				}
			})
		}
	}

	const fetchProductCategory = () => {
		if (currentStep === 0) {
			if (selectedProductLine !== 0) {
				setIsLoadingProductCategory(true)

				// Product Category Masterdata
				getOneWithChildren(selectedProductLine).then(({ success, data: dataGetOne }) => {
					if (!success) {
						Swal.fire({
							icon: 'error',
							title: 'ดึงข้อมูลไม่สำเร็จ',
							text: dataGetOne,
							confirmButtonText: 'ปิด',
							customClass: {
								confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
							},
						}).then(() => {
							setSelectingProduct(false)
						})
					} else {
						setIsLoadingProductCategory(false)
					}
				})
			}
		}
	}

	const mappingProperties = (
		_products: any[],
		_options: any[],
		_accessories: any[],
		_thicknesses: any[],
		_outerMaterials: any[],
		_innerMaterials: any[],
		_printingOptions: any[],
		_formings: any[]
	) => {
		setMappedProducts(
			_products.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedOptions(
			_options.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedAccessories(
			_accessories.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedThicknesses(
			_thicknesses.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedOuterMaterials(
			_outerMaterials.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedInnerMaterials(
			_innerMaterials.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedPrintingOptions(
			_printingOptions.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)

		setMappedFormings(
			_formings.map((o: any) => {
				return {
					...o,
					featurePicturePath: o.featurePicturePath
						? generateImageUrl(o.featurePicturePath)
						: undefined,
				}
			})
		)
	}

	const fetchProperties = () => {
		if (currentStep === 1) {
			setIsLoadingProperties(true)

			// Product Line Properties and Available Products
			Promise.all([
				getOneWithProperties(
					selectedProductCategory !== 0 ? selectedProductCategory : selectedProductLine
				),
				getProducts(selectedProductLine),
			]).then(
				([
					{ success: propertySuccess, data: propertyData },
					{ success: productSuccess, data: productData },
				]) => {
					if (!propertySuccess || !productSuccess) {
						Swal.fire({
							icon: 'error',
							title: 'ดึงข้อมูลไม่สำเร็จ',
							text: propertyData || productData,
							confirmButtonText: 'ปิด',
							customClass: {
								confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
							},
						}).then(() => {
							setSelectingProduct(false)
						})
					} else {
						mappingProperties(
							productData,
							propertyData.options,
							propertyData.accessories,
							propertyData.thicknesses,
							propertyData.outerMaterials,
							propertyData.innerMaterials,
							propertyData.printingOptions,
							propertyData.formings
						)
						setIsLoadingProperties(false)
					}
				}
			)
		}
	}

	const validateProperty = async () => {
		if (currentStep === 1) {
			return propertyValidationStep2.validate(propertyState, { abortEarly: false }).then(
				() => {
					setErrors({})
					return true
				},
				err => {
					const errorObject: any = {}
					err.inner.forEach((e: any) => {
						errorObject[e.path] = e.message
					})
					setErrors(errorObject)
					return false
				}
			)
		} else if (currentStep === 2) {
			return propertyValidationStep3.validate(propertyState, { abortEarly: false }).then(
				() => {
					setErrors({})
					return true
				},
				err => {
					const errorObject: any = {}
					err.inner.forEach((e: any) => {
						errorObject[e.path] = e.message
					})
					setErrors(errorObject)
					return false
				}
			)
		} else {
			return true
		}
	}

	const changeProductLineActive = (idx: number) => {
		const finded = productLineData.find((x, dataIdx) => dataIdx === idx)
		setSelectedProductLine(finded?.id ?? 0)
	}

	const changeProductCategoryActive = (idx: number) => {
		const finded = productCategoryData.find((x, dataIdx) => dataIdx === idx)
		setSelectedProductCategory(finded?.id ?? 0)
	}

	const selectProductLine = (id: number, hasChild: boolean) => {
		if (hasChild) {
			productCategoryRef.current?.scrollIntoView({ behavior: 'smooth' })
			setSelectedProductLine(selectedProductLine)
		} else {
			setCurrentStep(1)
		}
	}

	const selectProductCategory = (id: number) => {
		setCurrentStep(1)
	}

	const onSelectProperty = (property: string, value: any) => {
		switch (property) {
			case 'product': {
				setPropertyState({ ...propertyState, product: propertyState.product === value ? 0 : value })
				break
			}
			case 'options': {
				const finded = propertyState.options.findIndex(x => x === value)
				if (finded === -1) {
					setPropertyState({ ...propertyState, options: [...propertyState.options, value] })
				} else {
					setPropertyState({
						...propertyState,
						options: propertyState.options.filter(x => x !== value),
					})
				}
				break
			}
			case 'accessories': {
				const finded = propertyState.accessories.findIndex(x => x === value)

				if (finded === -1) {
					setPropertyState({ ...propertyState, accessories: [...propertyState.accessories, value] })
				} else {
					setPropertyState({
						...propertyState,
						accessories: propertyState.accessories.filter(x => x !== value),
					})
				}
				break
			}
			case 'thickness': {
				setPropertyState({
					...propertyState,
					thickness: propertyState.thickness === value ? 0 : value,
				})
				break
			}
			case 'outerMaterial': {
				setPropertyState({
					...propertyState,
					outerMaterial: propertyState.outerMaterial === value ? 0 : value,
				})
				break
			}
			case 'innerMaterial': {
				setPropertyState({
					...propertyState,
					innerMaterial: propertyState.innerMaterial === value ? 0 : value,
				})
				break
			}
			case 'printingOption': {
				setPropertyState({
					...propertyState,
					printingOption: propertyState.printingOption === value ? 0 : value,
				})
				break
			}
			case 'forming': {
				setPropertyState({ ...propertyState, forming: propertyState.forming === value ? 0 : value })
				break
			}
			case 'unit': {
				setPropertyState({ ...propertyState, unit: value })
				break
			}
			case 'measurementApproach': {
				setPropertyState({ ...propertyState, measurementApproach: value })
				break
			}
			case 'width': {
				const isNumber = /^\d+$/.test(value.toString())

				if (isNumber || value.toString() === '' || value.toString().includes('.')) {
					setPropertyState({ ...propertyState, width: value })
				}
				break
			}
			case 'length': {
				const isNumber = /^\d+$/.test(value.toString())

				if (isNumber || value.toString() === '' || value.toString().includes('.')) {
					setPropertyState({ ...propertyState, length: value })
				}
				break
			}
			case 'height': {
				const isNumber = /^\d+$/.test(value.toString())

				if (isNumber || value.toString() === '' || value.toString().includes('.')) {
					setPropertyState({ ...propertyState, height: value })
				}
				break
			}
			case 'weightGram': {
				const isNumber = /^\d+$/.test(value.toString())

				if (isNumber || value.toString() === '' || value.toString().includes('.')) {
					setPropertyState({ ...propertyState, weightGram: value })
				}
				break
			}
			case 'verticalStack': {
				const isNumber = /^\d+$/.test(value.toString())

				if (isNumber || value.toString() === '' || value.toString().includes('.')) {
					setPropertyState({ ...propertyState, verticalStack: value })
				}
				break
			}
			default: {
				return
			}
		}

		if (errors?.[property]) {
			setErrors({ ...errors, [property]: undefined })
		}
	}

	const clearState = () => {
		setSelectedWithChildren({})
		setSelectedWithProperties({})
		clearProductLineState()
		setPropertyState(INITIAL_STATE_PROPERTY)
	}

	const onPrevStep = async () => {
		if (currentStep === 1 && !isCreate) {
			setSelectingProduct(false)
		}
		// CASE 1: Create a quotation and already have a product
		// CASE 2: Create a quotation and have no product
		else if (
			(currentStep === 1 && isCreate && totalItems >= 1) ||
			(currentStep === 0 && isCreate)
		) {
			setSelectingProduct(false)

			onBack()
			setTimeout(() => {
				clearState()
			}, 300)
		} else {
			setCurrentStep(currentStep - 1)
		}
	}

	const onNextStep = async () => {
		validateProperty().then(isValid => {
			if (isValid) {
				// Product Line Selection
				if (currentStep === 0) {
					setCurrentStep(1)
					document
						.getElementById('product-form-quotation-anchor')
						?.scrollIntoView({ behavior: 'smooth' })
				}
				// Properties Selection
				else if (currentStep === 1) {
					setCurrentStep(2)
					document
						.getElementById('product-form-quotation-anchor')
						?.scrollIntoView({ behavior: 'smooth' })
				}
				// Measurement
				else if (currentStep === 2) {
					const findedProduct = mappedProducts.find(x => x.id === propertyState.product)
					const findedOptions = mappedOptions.filter(x => propertyState.options.includes(x.id))
					const findedAccessories = mappedAccessories.filter(x =>
						propertyState.accessories.includes(x.id)
					)
					const findedThickness = mappedThicknesses.find(x => x.id === propertyState.thickness)
					const findedOuterMaterial = mappedOuterMaterials.find(
						x => x.id === propertyState.outerMaterial
					)
					const findedInnerMaterial = mappedInnerMaterials.find(
						x => x.id === propertyState.innerMaterial
					)
					const findedPrintingOption = mappedPrintingOptions.find(
						x => x.id === propertyState.printingOption
					)
					const findedForming = mappedFormings.find(x => x.id === propertyState.forming)

					const finale: { [key: string]: any } = {
						product: {
							id: findedProduct?.id ?? 0,
							nameTh: findedProduct?.nameTh ?? '',
							nameEn: findedProduct?.nameEn ?? '',
							featurePicturePath: findedProduct?.featurePicturePath ?? '',
							code: findedProduct?.code ?? '',
							quantity: 1,
							shortDescription: findedProduct?.shortDescription ?? '',

							productLine: {
								id: selectedWithChildren?.id ?? 0,
								nameTh: selectedWithChildren?.nameTh ?? '',
								nameEn: selectedWithChildren?.nameEn ?? '',
								featurePicturePath: selectedWithChildren?.featurePicturePath ?? null,
								parent: selectedWithChildren?.parent
									? {
											id: selectedWithChildren?.parent?.id ?? 0,
											nameTh: selectedWithChildren?.parent?.nameTh ?? '',
											nameEn: selectedWithChildren?.parent?.nameEn ?? '',
											featurePicturePath: selectedWithChildren?.parent?.featurePicturePath ?? null,
									  }
									: undefined,
							},
						},
						options: findedOptions,
						accessories: findedAccessories,
						thickness: findedThickness ?? undefined,
						outerMaterial: findedOuterMaterial ?? undefined,
						innerMaterial: findedInnerMaterial ?? undefined,
						printingOption: findedPrintingOption ?? undefined,
						forming: findedForming ?? undefined,
						dimension: {
							width: propertyState.width,
							length: propertyState.length,
							height: propertyState.height,
							weightGram: propertyState.weightGram,
							verticalStack: propertyState.verticalStack,
							unit: propertyState.unit,
							measurementApproach: propertyState.measurementApproach,
						},
					}

					onConfirmAddProduct(finale as any)
					setSelectingProduct(false)

					setTimeout(() => {
						clearState()
					}, 300)
				}
				// Unknown
				else {
					Swal.fire({
						icon: 'error',
						title: 'ไม่สามารถดำเนินการต่อได้',
						text: 'กรุณาโหลดข้อมูลใหม่อีกครั้ง',
						confirmButtonText: 'ปิด',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					}).then(() => {
						setSelectingProduct(false)
					})
				}
			} else {
				toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
			}
		})
	}

	useEffect(() => {
		handleStartSelectingProduct()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelectingProduct, canSelectProductLine, formType])

	useEffect(() => {
		fetchProductLines()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep])

	useEffect(() => {
		fetchProductCategory()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, selectedProductLine])

	useEffect(() => {
		fetchProperties()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, selectedProductCategory, selectedProductLine])

	useEffect(() => {
		return () => {
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		// Status
		isLoadingProductLine,
		isLoadingProductCategory,
		isLoadingProperties,
		// Data
		currentStep,
		selectedProductLine,
		selectedProductCategory,
		currentActiveProductLine,
		currentActiveProductCategory,
		productLineData,
		productCategoryData,
		propertyState,
		mappedOptions,
		mappedProducts,
		mappedAccessories,
		mappedThicknesses,
		mappedOuterMaterials,
		mappedInnerMaterials,
		mappedPrintingOptions,
		mappedFormings,
		errors,
		showPrevButton,
		// Function
		changeProductLineActive,
		changeProductCategoryActive,
		selectProductLine,
		selectProductCategory,
		productCategoryRef,
		onSelectProperty,
		onPrevStep,
		onNextStep,
	}
}

export default ViewModel
